import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { HttpClient, Session } from "../api";
import { canWrite as levelCanWrite, hasView as accountHasView, type AccountDTO } from "../api/account";

type AuthStatus = "booting" | "authenticated" | "anonymous";

interface AuthContextValue {
  status: AuthStatus;
  account: AccountDTO | null;
  http: HttpClient;
  session: Session;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Re-reads `/auth/me`. The account is cached for the session, so a screen
   * that edits it — the profile page — has to say so or the header keeps
   * showing the old picture until the next reload.
   */
  reload: () => Promise<AccountDTO>;
  /** True for ROOT and ADMIN. Drives the read-only UX for USER accounts. */
  canWrite: boolean;
  /** View-code check with the ROOT bypass built in. */
  hasView: (code: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  session: Session;
  http: HttpClient;
}

export function AuthProvider({ children, session, http }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>("booting");
  const [account, setAccount] = useState<AccountDTO | null>(null);

  const loadAccount = useCallback(async () => {
    // `/auth/me` returns the account with its view codes in one round-trip.
    const me = await http.get<AccountDTO>("/auth/me");
    setAccount(me);
    return me;
  }, [http]);

  // On boot the access token is gone (it only ever lived in memory), so spend
  // the refresh cookie once to find out whether this is still a live session.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const recovered = await session.bootstrap();
      if (cancelled) return;

      if (!recovered) {
        setStatus("anonymous");
        return;
      }

      try {
        await loadAccount();
        if (!cancelled) setStatus("authenticated");
      } catch {
        if (!cancelled) setStatus("anonymous");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session, loadAccount]);

  const login = useCallback(
    async (username: string, password: string) => {
      await session.login(username, password);
      await loadAccount();
      setStatus("authenticated");
    },
    [session, loadAccount],
  );

  const logout = useCallback(async () => {
    await session.logout();
    setAccount(null);
    setStatus("anonymous");
  }, [session]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      account,
      reload: loadAccount,
      http,
      session,
      login,
      logout,
      canWrite: levelCanWrite(account),
      hasView: (code: string) => accountHasView(account, code),
    }),
    [status, account, http, session, login, logout, loadAccount],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside an AuthProvider");
  }
  return context;
}

/**
 * UX-only write gating, the direct replacement for `WriteGate`. The server is
 * still the enforcement point — this only stops USER accounts from being shown
 * controls that would 403.
 */
export function useCanWrite(): boolean {
  return useAuth().canWrite;
}
